<script setup>
import { SOCIAL_SHARE_IMAGE, usePageHead } from '@composables/use-page-head'
import { computed, onMounted, ref } from 'vue'
import { useNowPlayingStore } from '@state/stores/now-playing'
import DataLayer from '@services/data-layer/index'
import Layout from '@layouts/main-layout'
import ChannelDetailBlock from '@components/channel/channel-detail-block'
import PremiumBanner from '@components/premium-banner'

const CHANNELS_ORDER = ['chill', 'classics', 'deep', 'frisky']
const PLACEHOLDER_CHANNELS = CHANNELS_ORDER.length

defineProps({
    isFavorites: {
        type: Boolean,
        default: false,
    },
})

const nowPlayingStore = useNowPlayingStore()

const channels = ref(Array.from(Array(PLACEHOLDER_CHANNELS), (_channel, id) => ({ id })))

const playlists = computed(() => nowPlayingStore.byStation)

onMounted(() => {
    DataLayer.channels.get().then(response => {
        channels.value = Object.values(response.body).sort(
            (a, b) => CHANNELS_ORDER.indexOf(a.id) - CHANNELS_ORDER.indexOf(b.id)
        )
    })
})

usePageHead({
    title: 'Radio',
    description: 'Underground electronic radio channels — house, techno, progressive, deep house',
    image: SOCIAL_SHARE_IMAGE,
})
</script>

<template>
    <Layout
        v-if="channels && channels.length"
        hidden-overflow
    >
        <BaseContent title="Radio">
            <div class="premium-banner-inset">
                <PremiumBanner source="radio-banner" />
            </div>
            <ChannelDetailBlock
                :channels="channels"
                :playlists="playlists"
                :is-favorites="isFavorites"
            />
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

// On the radio page the channel carousel breaks out to the full content width,
// so the banner would otherwise stretch full-bleed. The Figma (816:406) insets
// it: a fixed 1005px creative centered in the content (matches the banner's
// fixed-size art). Below 1005px of content (mobile/tablet) it stays full-width.
// Named for the wrapper rather than the component: a scoped `.premium-banner`
// would also match PremiumBanner's own root, which carries this scope id too.
.premium-banner-inset {
    max-width: 1005px;
    margin-right: auto;
    margin-left: auto;
}
</style>
